import { useCallback, useEffect, useState, type FormEvent } from "react";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Contact, type Job, type PipelineCard, type Shortlist, type ShortlistDecision } from "../lib/api";
import { whenLast } from "../lib/dates";
import { displaySkill } from "../lib/format";

const pad = (n: number) => String(n).padStart(2, "0");
export const DECISION_LABEL: Record<ShortlistDecision, string> = { interview: "Interview", maybe: "Maybe", no: "Not for us" };
const dayFmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

/** The client opens this; the token sits after # so it never reaches a server log. */
export const shortlistLink = (token: string) => `${window.location.origin}/shortlist#${token}`;

type Pick = { pitch: string; includeCv: boolean; consent: boolean };

/**
 * Shortlists sent to the client as a private link: who's on each, whether the client has opened
 * it, and their answers. "Send a shortlist" picks candidates from the pipeline.
 */
export function JobShortlists({ job, changed }: { job: Job; changed: number }) {
  const [list, setList] = useState<Shortlist[] | null>(null);
  const [composing, setComposing] = useState(false);
  const [link, setLink] = useState<{ token: string; contact: Contact | null } | null>(null);
  const load = useAction();
  const act = useAction();

  const reload = useCallback(
    () => load.run(async () => setList((await api.shortlists(job.id)).shortlists)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [job.id, changed],
  );
  useEffect(() => void reload(), [reload]);

  const open = job.status === "open" || job.status === "on_hold";

  return (
    <section className="section" aria-labelledby="shortlists-h">
      <div className="section-head">
        <h2 id="shortlists-h" className="label">
          Sent to the client
        </h2>
        {open && !composing && !link && (
          <button type="button" className="link-btn" onClick={() => setComposing(true)}>
            + Send a shortlist
          </button>
        )}
      </div>
      <ErrorNotice error={load.error ?? act.error} />

      {link && <LinkReady job={job} token={link.token} contact={link.contact} onDone={() => setLink(null)} />}
      {composing && (
        <Compose
          job={job}
          onCancel={() => setComposing(false)}
          onCreated={(token, contact) => {
            setComposing(false);
            setLink({ token, contact });
            void reload();
          }}
        />
      )}

      {list && list.length === 0 && !composing && !link && (
        <p className="hint">
          Send the client a private link with the candidates you're putting forward, a line on each and their CV. They answer
          Interview, Maybe or Not for us, and it shows up here and on Today.
        </p>
      )}
      {list && list.length > 0 && (
        <ol className="index shortlists">
          {list.map((s, i) => (
            <li key={s.id} className={s.revoked ? "inactive" : undefined}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <span className="primary">
                  {s.contact ? `For ${s.contact.fullName}` : "Shortlist"} · {s.candidates.length} candidate{s.candidates.length === 1 ? "" : "s"}
                </span>
                <span className="secondary">
                  Sent {whenLast(s.createdAt)} by {s.createdBy} · {s.firstViewedAt ? `opened ${whenLast(s.lastViewedAt ?? s.firstViewedAt)}` : "not opened yet"} ·{" "}
                  {s.revoked ? "link off" : `link works until ${dayFmt.format(new Date(s.expiresAt))}`}
                </span>
                <ul className="answers">
                  {s.candidates.map((c) => (
                    <li key={c.id}>
                      <span>{c.candidateName}</span>{" "}
                      {c.decision ? (
                        <span className={`answer ${c.decision}`}>{DECISION_LABEL[c.decision]}</span>
                      ) : (
                        <span className="secondary">no answer yet</span>
                      )}
                      {c.feedback && <span className="secondary"> “{c.feedback}”</span>}
                    </li>
                  ))}
                </ul>
              </span>
              <span className="actions">
                <button
                  type="button"
                  className="link-btn"
                  disabled={act.pending}
                  onClick={() =>
                    void act.run(async () => {
                      const { token } = await api.newShortlistLink(s.id);
                      setLink({ token, contact: null });
                      await reload();
                    })
                  }
                >
                  New link
                </button>
                {!s.revoked && (
                  <button
                    type="button"
                    className="link-btn danger"
                    disabled={act.pending}
                    onClick={() =>
                      void act.run(async () => {
                        await api.revokeShortlist(s.id);
                        await reload();
                      })
                    }
                  >
                    Turn off
                  </button>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** The link, shown once: copy it, email it, or look at it as the client will. */
function LinkReady({ job, token, contact, onDone }: { job: Job; token: string; contact: Contact | null; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = shortlistLink(token);
  const first = contact?.fullName.replace(/^(dr|mr|mrs|ms|miss|prof)\.?\s+/i, "").split(" ")[0];
  const mail = `mailto:${contact?.email ?? ""}?subject=${encodeURIComponent(`Candidates for ${job.title}`)}&body=${encodeURIComponent(
    `Hi${first ? ` ${first}` : ""},\n\nHere are the candidates I'd like to put forward for ${job.title}:\n${url}\n\nYou can open their CVs and tell me who you'd like to meet on that page.\n\nThanks`,
  )}`;
  return (
    <div className="form copy-block">
      <p className="notice ok">The link is ready. Copy it now: it won't be shown again (you can always make a new one).</p>
      <div className="copy-row">
        <code>{url}</code>
        <button type="button" className="btn secondary" onClick={() => void navigator.clipboard.writeText(url).then(() => setCopied(true))}>
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <div className="button-row">
        <a className="link-btn" href={mail}>
          Email it{contact?.email ? ` to ${contact.fullName}` : ""}
        </a>
        <a className="link-btn" href={url} target="_blank" rel="noopener">
          See it as the client will
        </a>
        <button type="button" className="link-btn" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}

/**
 * A first draft of the line about each candidate. The client already sees their title, town,
 * notice and salary, so the draft only says which of the job's skills they bring; the recruiter
 * adds why they fit.
 */
async function draftPitch(candidateId: string, job: Job): Promise<string> {
  const { candidate: c } = await api.candidate(candidateId);
  const matching = c.skills.filter((s) => job.skills.includes(s));
  return matching.length ? `Strong on ${matching.slice(0, 5).map(displaySkill).join(", ")}. ` : "";
}

function Compose({ job, onCancel, onCreated }: { job: Job; onCancel: () => void; onCreated: (token: string, contact: Contact | null) => void }) {
  const [cards, setCards] = useState<PipelineCard[] | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactId, setContactId] = useState(job.hiringContact?.id ?? "");
  const [message, setMessage] = useState("");
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const [picked, setPicked] = useState<Record<string, Pick>>({});
  const { pending, error, setError, run } = useAction();

  useEffect(() => {
    void run(async () => {
      const [board, people] = await Promise.all([api.pipeline(job.id), api.contacts(job.company.id)]);
      setCards(board.cards.filter((c) => c.status === "active" && !c.candidate.erased));
      setContacts(people.contacts.filter((c) => !c.archived));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const contact = contacts.find((c) => c.id === contactId) ?? null;
  useEffect(() => {
    const first = contact?.fullName.replace(/^(dr|mr|mrs|ms|miss|prof)\.?\s+/i, "").split(" ")[0];
    setMessage(`Hi${first ? ` ${first}` : ""}, here are the people I'd like to put forward for ${job.title}. Let me know who you'd like to meet.`);
  }, [contact?.fullName, job.title]);

  const toggle = (card: PipelineCard) => {
    if (picked[card.id]) {
      setPicked(({ [card.id]: _gone, ...rest }) => rest);
      return;
    }
    setPicked((p) => ({ ...p, [card.id]: { pitch: "", includeCv: card.candidate.hasCv, consent: false } }));
    void draftPitch(card.candidate.id, job)
      .then((pitch) =>
        setPicked((p) => {
          const current = p[card.id];
          return current && !current.pitch ? { ...p, [card.id]: { ...current, pitch } } : p;
        }),
      )
      .catch(() => {});
  };
  const edit = (id: string, change: Partial<Pick>) => setPicked((p) => ({ ...p, [id]: { ...p[id]!, ...change } }));

  const chosen = (cards ?? []).filter((c) => picked[c.id]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!chosen.length) return setError("Choose at least one candidate.");
    const missing = chosen.find((c) => !picked[c.id]!.consent);
    if (missing) return setError(`Confirm ${missing.candidate.fullName} agreed to be put forward for this role.`);
    void run(async () => {
      const { token } = await api.createShortlist(job.id, {
        contactId: contactId || null,
        message: message.trim(),
        expiresInDays: days,
        candidates: chosen.map((c) => ({ applicationId: c.id, pitch: picked[c.id]!.pitch.trim(), includeCv: picked[c.id]!.includeCv, consentConfirmed: true })),
      });
      onCreated(token, contact);
    });
  };

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="grid-2">
        <label className="field">
          <span className="label">For</span>
          <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
            <option value="">No one in particular</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.fullName}
                {c.jobTitle ? ` (${c.jobTitle})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Link works for</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value) as 7 | 30 | 90)}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        </label>
      </div>
      <label className="field">
        <span className="label">Message at the top</span>
        <textarea rows={2} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
      </label>

      <fieldset className="pick-candidates">
        <legend className="label">Who to put forward</legend>
        {cards && cards.length === 0 && <p className="hint">No one is in process on this job yet. Shortlist candidates first.</p>}
        {cards?.map((card) => {
          const p = picked[card.id];
          return (
            <div key={card.id} className={`pick ${p ? "on" : ""}`}>
              <label className="check">
                <input type="checkbox" checked={!!p} onChange={() => toggle(card)} />
                <span>
                  <strong>{card.candidate.fullName}</strong>
                  {card.candidate.currentTitle && <span className="secondary"> · {card.candidate.currentTitle}</span>}
                </span>
              </label>
              {p && (
                <div className="pick-detail">
                  <label className="field">
                    <span className="label">What to tell the client</span>
                    <textarea rows={3} maxLength={5000} value={p.pitch} onChange={(e) => edit(card.id, { pitch: e.target.value })} placeholder="Why they fit this role" />
                  </label>
                  {card.candidate.hasCv ? (
                    <label className="check">
                      <input type="checkbox" checked={p.includeCv} onChange={(e) => edit(card.id, { includeCv: e.target.checked })} />
                      <span>Include their CV</span>
                    </label>
                  ) : (
                    <p className="hint">No CV on file.</p>
                  )}
                  <label className="check">
                    <input type="checkbox" checked={p.consent} onChange={(e) => edit(card.id, { consent: e.target.checked })} />
                    <span>They've agreed to be put forward for this role</span>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </fieldset>
      <p className="hint">The client sees each person's name, title, location, notice, salary, skills, your note and the CV if included. Not their email, phone or employer.</p>
      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending || !chosen.length}>
          {pending ? "Creating…" : `Create link${chosen.length ? ` for ${chosen.length}` : ""}`}
        </button>
        <button type="button" className="link-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
